import { Building2, Home, KeyRound } from "lucide-react";
import { Reveal } from "./Reveal";

export const ModelCompare = () => (
  <section id="model" className="bg-paper py-20 md:py-28" data-testid="model-section">
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <Reveal>
        <p className="font-spec text-[11px] uppercase tracking-[0.25em] text-amberdark">The comparison</p>
        <h2 className="mt-5 max-w-3xl font-display text-3xl font-extrabold uppercase leading-[1.08] tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="model-headline">
          Same capital. <span className="text-amber">Very different outcome.</span>
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-steel">
          If you invest ₹4 Cr in a premium apartment in a premium location, what
          rental income would you realistically expect?
        </p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="model-compare-grid">
        <Reveal delay={0.1}>
          <div className="h-full rounded-3xl border border-border bg-stone p-8 md:p-10" data-testid="model-usual-card">
            <p className="font-spec text-[10px] uppercase tracking-[0.22em] text-steel">The usual route</p>
            <p className="mt-4 font-display text-2xl font-extrabold tracking-tight text-navy">₹4 Cr premium apartment</p>
            <p className="mt-5 font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl" data-testid="model-usual-figure">
              ₹80,000–₹1,00,000<span className="text-lg font-bold text-steel">/month</span>
            </p>
            <p className="mt-3 text-sm leading-relaxed text-steel">
              Typical expectation — with ₹1.2–₹1.5 lakh per month being a strong
              outcome, depending on location, property and market.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="relative h-full overflow-hidden rounded-3xl bg-ink p-8 md:p-10" data-testid="model-uv-card">
            <span className="absolute right-6 top-6 rounded-full bg-amber/15 px-3 py-1.5 font-spec text-[9px] uppercase tracking-[0.18em] text-amber">
              Target — not a guarantee
            </span>
            <p className="font-spec text-[10px] uppercase tracking-[0.22em] text-white/50">The Urban Veera property model</p>
            <p className="mt-4 font-display text-2xl font-extrabold tracking-tight text-white">Land + building ownership</p>
            <p className="mt-5 font-display text-4xl font-extrabold tracking-tight text-amber sm:text-5xl" data-testid="model-uv-figure">
              ~₹7–9 lakh<span className="text-lg font-bold text-white/60">/month target</span>
            </p>
            <p className="mt-3 text-sm leading-relaxed text-white/70">
              Depending on the project and your requirements, our property
              models can target rental income of approximately ₹7–9 lakh per
              month — through land plus building ownership, not just an apartment.
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.25}>
        <div className="mt-6 grid grid-cols-1 gap-6 rounded-3xl border border-border bg-mist p-8 md:grid-cols-3 md:p-10" data-testid="model-ownership-strip">
          {[
            { icon: KeyRound, text: "The land is in your name." },
            { icon: Home, text: "The building is in your name." },
            { icon: Building2, text: "The rental income comes from the property you own." },
          ].map((o, i) => (
            <div key={i} className="flex items-center gap-4" data-testid={`model-ownership-${i}`}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-amberdark shadow-soft">
                <o.icon size={18} />
              </span>
              <p className="text-sm font-bold leading-snug text-navy">{o.text}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.3}>
        <p className="mt-6 max-w-3xl text-[11px] leading-relaxed text-steel/80" data-testid="model-hedge">
          Every project is structured based on the specific property, location,
          construction cost, market conditions and customer requirements. Rental
          figures shown are illustrative targets, not assured income.
        </p>
      </Reveal>
    </div>
  </section>
);
